// @ts-check
/**
 * Wholesale tracker: every retail partner KAAY approaches, with the pipeline as a list or a board
 * (one page, toggled), the follow-ups due, and a ranking of all quotes by net per reference piece.
 * Super Admin only; see the Stockist Outreach Tracker doc.
 */
import { Download, LayoutGrid, List, Mail, Plus, RefreshCw } from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import LoadingScreen from '../components/LoadingScreen';
import GmailRepliesPanel from '../components/wholesale/GmailRepliesPanel';
import OutreachTab from '../components/wholesale/OutreachTab';
import PartnerFormModal from '../components/wholesale/PartnerFormModal';
import PartnersBoard from '../components/wholesale/PartnersBoard';
import PartnersTable from '../components/wholesale/PartnersTable';
import QuoteRanking from '../components/wholesale/QuoteRanking';
import RemindersPanel from '../components/wholesale/RemindersPanel';
import { EnumSelect, input, primaryBtn, saveBlob, secondaryBtn } from '../components/wholesale/shared';
import { useToast } from '../context/ToastContext';
import adminWholesaleService from '../services/adminWholesaleService';
import { WHOLESALE_PARTNER_KIND, WHOLESALE_PRIORITY, WHOLESALE_STAGE } from '../utils/wholesale';

const VIEW_KEY = 'wholesaleView';

const readView = () => {
  try {
    return localStorage.getItem(VIEW_KEY) === 'board' ? 'board' : 'list';
  } catch {
    return 'list';
  }
};

const WholesalePage = () => {
  const toast = useToast();
  const [tab, setTab] = useState('partners');
  const [view, setView] = useState(readView);
  const [partners, setPartners] = useState([]);
  const [reminders, setReminders] = useState([]);
  const [ranking, setRanking] = useState(null);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const [filters, setFilters] = useState({ kind: '', stage: '', priority: '', city: '', q: '' });
  const [showArchived, setShowArchived] = useState(false);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const [list, due, rank] = await Promise.all([
        adminWholesaleService.listPartners({ ...filters, include_archived: showArchived || undefined }),
        adminWholesaleService.listReminders(),
        adminWholesaleService.ranking(),
      ]);
      setPartners(list);
      setReminders(due);
      setRanking(rank);
    } catch (err) {
      toast.error(err.message || 'Failed to load the wholesale tracker');
    } finally {
      setLoading(false);
    }
  }, [filters, showArchived, toast]);

  useEffect(() => {
    load();
  }, [load]);

  const chooseView = (next) => {
    setView(next);
    try {
      localStorage.setItem(VIEW_KEY, next);
    } catch {
      // Remembering the view is a convenience; the page works without it.
    }
  };

  const set = (patch) => setFilters((prev) => ({ ...prev, ...patch }));

  const download = async (kind) => {
    try {
      const blob = await (kind === 'partners'
        ? adminWholesaleService.exportPartners()
        : adminWholesaleService.exportQuotes());
      saveBlob(blob, `wholesale-${kind}.csv`);
    } catch (err) {
      toast.error(err.message || 'Export failed');
    }
  };

  const sendDigest = async () => {
    try {
      const { reminders_emailed: count } = await adminWholesaleService.sendDigest();
      toast.success(
        count ? `Emailed ${count} follow-up${count === 1 ? '' : 's'}` : 'Nothing is due, so nothing was sent',
      );
    } catch (err) {
      toast.error(err.message || 'Could not send the digest');
    }
  };

  const counts = useMemo(() => {
    const byStage = {};
    partners.forEach((p) => {
      byStage[p.stage] = (byStage[p.stage] || 0) + 1;
    });
    return byStage;
  }, [partners]);

  return (
    <div>
      <div className="flex flex-wrap justify-between items-center gap-3 mb-6">
        <div>
          <h1 className="text-3xl font-serif">Wholesale</h1>
          <p className="text-sm text-muted-foreground mt-1">
            {partners.length} partners · {(counts[WHOLESALE_STAGE.SIGNED] || 0) + (counts[WHOLESALE_STAGE.LIVE] || 0)}{' '}
            signed or live
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <button onClick={load} disabled={loading} className={`${secondaryBtn} flex items-center gap-2`}>
            <RefreshCw size={16} className={loading ? 'animate-spin' : ''} /> Refresh
          </button>
          <button onClick={() => download('partners')} className={`${secondaryBtn} flex items-center gap-2`}>
            <Download size={16} /> Partners CSV
          </button>
          <button onClick={() => download('quotes')} className={`${secondaryBtn} flex items-center gap-2`}>
            <Download size={16} /> Quotes CSV
          </button>
          <button
            onClick={sendDigest}
            className={`${secondaryBtn} flex items-center gap-2`}
            title="Email today's follow-ups to the owner now"
          >
            <Mail size={16} /> Email digest
          </button>
          <button onClick={() => setAdding(true)} className={`${primaryBtn} flex items-center gap-2`}>
            <Plus size={16} /> Add partner
          </button>
        </div>
      </div>

      <GmailRepliesPanel />

      <RemindersPanel reminders={reminders} onChanged={load} />

      <div className="flex gap-1 bg-muted rounded-lg p-1 mb-6 max-w-md">
        {[
          { key: 'partners', label: 'Partners' },
          { key: 'quotes', label: 'Quote ranking' },
          { key: 'outreach', label: 'Outreach' },
        ].map((t) => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            className={`flex-1 whitespace-nowrap px-4 py-2 text-sm font-medium rounded-lg transition ${
              tab === t.key ? 'bg-card text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'outreach' && <OutreachTab />}

      {tab === 'quotes' && (loading && !ranking ? <LoadingScreen /> : <QuoteRanking data={ranking} />)}

      {tab === 'partners' && (
        <>
          <div className="flex flex-wrap items-center gap-2 mb-4">
            <input
              className={`${input} !w-52`}
              placeholder="Search name or notes"
              value={filters.q}
              onChange={(e) => set({ q: e.target.value })}
            />
            <div className="w-40">
              <EnumSelect
                value={filters.kind}
                onChange={(kind) => set({ kind })}
                options={Object.values(WHOLESALE_PARTNER_KIND)}
                allowEmpty
                emptyLabel="All kinds"
                aria-label="Kind"
              />
            </div>
            <div className="w-44">
              <EnumSelect
                value={filters.stage}
                onChange={(stage) => set({ stage })}
                options={Object.values(WHOLESALE_STAGE)}
                allowEmpty
                emptyLabel="All stages"
                aria-label="Stage"
              />
            </div>
            <div className="w-40">
              <EnumSelect
                value={filters.priority}
                onChange={(priority) => set({ priority })}
                options={Object.values(WHOLESALE_PRIORITY)}
                allowEmpty
                emptyLabel="All priorities"
                aria-label="Priority"
              />
            </div>
            <input
              className={`${input} !w-36`}
              placeholder="City"
              value={filters.city}
              onChange={(e) => set({ city: e.target.value })}
            />
            <label className="flex items-center gap-2 text-sm text-muted-foreground">
              <input type="checkbox" checked={showArchived} onChange={(e) => setShowArchived(e.target.checked)} />
              Archived
            </label>
            <div className="ml-auto flex gap-1 bg-muted rounded-lg p-1">
              {[
                { key: 'list', icon: List, label: 'List' },
                { key: 'board', icon: LayoutGrid, label: 'Board' },
              ].map(({ key, icon: Icon, label: text }) => (
                <button
                  key={key}
                  onClick={() => chooseView(key)}
                  aria-pressed={view === key}
                  className={`flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-md transition ${
                    view === key ? 'bg-card text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'
                  }`}
                >
                  <Icon size={15} /> {text}
                </button>
              ))}
            </div>
          </div>

          {loading && partners.length === 0 ? (
            <LoadingScreen />
          ) : partners.length === 0 ? (
            <div className="bg-card border rounded-lg border-border p-12 text-center text-muted-foreground">
              No partners match. Add one, or clear the filters.
            </div>
          ) : view === 'board' ? (
            <PartnersBoard partners={partners} />
          ) : (
            <PartnersTable partners={partners} />
          )}
        </>
      )}

      {adding && (
        <PartnerFormModal
          onClose={() => setAdding(false)}
          onSaved={() => {
            setAdding(false);
            load();
          }}
        />
      )}
    </div>
  );
};

export default WholesalePage;
